import { WorldSituation } from './WorldSituation.js';
import { ConsequencePreview } from './ConsequencePreview.js';
import { SafeMarkdown } from '../../components/SafeMarkdown.js';
import { SponsorInterlude } from './SponsorInterlude.js';
import { RoundAssistantExchange } from './RoundAssistantExchange.js';
import { GmHandExchange } from './GmHandExchange.js';
import { parseHands } from './gm-hand-assistant.js';
import { TeamAssistantExchange } from './TeamAssistantExchange.js';
import { actionFields, profileFields } from './team-assistant.js';
import { manualResolutionContentSchema } from '@nerds-at-play/contracts';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type {
  ManualResolutionDraft,
  BoardSnapshot,
  BoardTeam,
  ActionBrief,
  ActionBriefWorkspace,
  ManualResolutionContent,
  FinalRecapContent,
} from '@nerds-at-play/contracts';
import { entryApi, entryMessage } from './formation-api.js';
import './live.css';
const pendingCommands = new Map<string, string>();
async function api<T>(path: string, body?: unknown, method = 'POST'): Promise<T> {
  const fingerprint = JSON.stringify({ path, body, method });
  const key = pendingCommands.get(fingerprint) ?? crypto.randomUUID();
  pendingCommands.set(fingerprint, key);
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { 'content-type': 'application/json', 'idempotency-key': key },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.ok || response.status < 500) pendingCommands.delete(fingerprint);
  const data = (await response.json()) as { message?: string; findings?: { message: string }[] };
  if (!response.ok)
    throw new Error(
      data.message +
        (data.findings
          ? ' ' + data.findings.map((f: { message: string }) => f.message).join(' ')
          : ''),
    );
  return data as T;
}
function briefText(brief: unknown, key: string) {
  const value =
    brief && typeof brief === 'object' ? (brief as Record<string, unknown>)[key] : undefined;
  return typeof value === 'string' ? value : '';
}
function EventTime({
  at,
  missing = 'Time not recorded',
  label,
}: {
  at?: string | undefined;
  missing?: string;
  label: string;
}) {
  return (
    <span className="live-event-time">
      {at ? (
        <time dateTime={at} title={label + ' · ' + new Date(at).toLocaleString()}>
          {new Date(at).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
          })}
        </time>
      ) : (
        <small>{missing}</small>
      )}
    </span>
  );
}
type Editor = {
  kind: 'profile' | 'move' | 'hand' | 'resolution' | 'reflection' | 'recap' | 'finish';
  team?: BoardTeam | undefined;
  version: number;
  workspaceVersion: number;
  roundVersion: number;
  roundNo: number | null;
  values: Record<string, string>;
  review: boolean;
  externalAssistance?: ManualResolutionDraft['externalAssistance'];
};
export function LiveBoard({
  role,
  teamId,
  onUnauthorized,
  onManageTeams,
}: {
  role: string;
  teamId?: string | undefined;
  onUnauthorized: () => void;
  onManageTeams: () => void;
}) {
  const [board, setBoard] = useState<BoardSnapshot>();
  const [error, setError] = useState('');
  const [connectionError, setConnectionError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [editor, setEditor] = useState<Editor>();
  const dialog = useRef<HTMLDialogElement>(null);
  const refresh = async () => {
    try {
      const b = await entryApi<BoardSnapshot>('/api/board');
      setBoard(b);
      setConnectionError('');
      return b;
    } catch (e) {
      setConnectionError(entryMessage(e));
      if (e instanceof Error && 'status' in e && e.status === 401) onUnauthorized();
      return undefined;
    }
  };
  useEffect(() => {
    let active = true;
    const poll = async () => {
      if (active) await refresh();
    };
    void poll();
    const timer = setInterval(() => void poll(), 2000);
    return () => {
      active = false;
      clearInterval(timer);
      pendingCommands.clear();
    };
  }, []); // session remount owns lifetime
  const run = async (fn: () => Promise<unknown>, message = 'Saved.') => {
    setBusy(true);
    setError('');
    try {
      await fn();
      setNotice(message);
      await refresh();
      return true;
    } catch (e) {
      setError(entryMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  if (!board) return <p role="status">{connectionError || 'Loading the game…'}</p>;
  const b = board,
    g = b.game,
    gm = role === 'MODERATOR',
    own = b.teams.find((t) => t.teamId === teamId),
    hand = own?.assignment?.assignment,
    base = `/api/moderator/games/${g.gameId}`,
    teamBase = `/api/games/${g.gameId}`;
  const open = (kind: Editor['kind'], team?: BoardTeam) => {
    let values: Record<string, string> = {};
    if (kind === 'profile')
      values = {
        name: own?.name ?? '',
        organizationType: 'Civic organization',
        mission: '',
        desiredFuture: '',
        activities: '',
        realWorldAnalogues: 'Fictional organization',
        approximateScale: 'Regional',
        ...Object.fromEntries(
          Object.entries(own?.organization ?? {}).filter(
            ([k, v]) => k in profileFields && typeof v === 'string',
          ),
        ),
      };
    if (kind === 'move')
      values = {
        action: '',
        why: '',
        strategy: '',
        resourcesCommitted: '',
        organizationsInvolved: 'Our organization',
        intendedOutcome: '',
        timeHorizon: 'This round',
        ...b.workspace?.currentDraft,
      };
    if (kind === 'resolution' && b.gm) {
      const c = b.gm.draft?.content ?? b.gm.manualTemplate;
      values = {
        summary: c.summary,
        headline: c.broadcast.headline,
        publicMarkdown: c.broadcast.publicMarkdown,
        world: JSON.stringify(c.nextState.publicWorld, null, 2),
        teams: JSON.stringify(c.nextState.teams, null, 2),
        privateBulletins: JSON.stringify(c.privateBulletins, null, 2),
      };
    }
    if (kind === 'reflection')
      values = {
        reflection: b.reflection?.whatMatteredMost ?? '',
        score: String(b.reflection?.score ?? 0),
      };
    if (kind === 'recap')
      values = { finalState: b.recap?.content.finalState ?? JSON.stringify(b.world, null, 2) };
    if (kind === 'finish') values = { reason: '' };
    setEditor({
      kind,
      ...((team ?? own) ? { team: team ?? own } : {}),
      roundNo: g.activeRoundNo,
      version: g.version,
      workspaceVersion: b.workspace?.workspaceVersion ?? 0,
      roundVersion: b.gm?.roundVersion ?? 0,
      values,
      review: kind === 'move' && !!b.workspace?.currentDraft && !b.workspace.officialSubmission,
      ...(kind === 'resolution' && b.gm?.draft?.externalAssistance
        ? { externalAssistance: b.gm.draft.externalAssistance }
        : {}),
    });
    dialog.current?.showModal();
  };
  const set = (key: string, value: string) =>
    setEditor((e) => (e ? { ...e, review: false, values: { ...e.values, [key]: value } } : e));
  const close = () => {
    dialog.current?.close();
    setEditor(undefined);
  };
  const save = async (official = false) => {
    if (!editor) return;
    const e = editor,
      v = e.values;
    let savedWorkspace: ActionBriefWorkspace | undefined;
    const success = await run(
      async () => {
        if (e.kind === 'profile')
          await api(
            `${teamBase}/organization`,
            { expectedTeamVersion: e.team?.version, profile: v, confirm: true },
            'PUT',
          );
        if (e.kind === 'move')
          savedWorkspace = await api<ActionBriefWorkspace>(
            `${teamBase}/rounds/${e.roundNo}/submissions`,
            {
              action: official ? 'SUBMIT' : 'SAVE_DRAFT',
              expectedWorkspaceVersion: e.workspaceVersion,
              brief: v,
            },
          );
        if (e.kind === 'resolution' && b.gm) {
          const content: ManualResolutionContent = manualResolutionContentSchema.parse({
            summary: v['summary'] ?? '',
            nextState: {
              ...b.gm.currentState,
              version: b.gm.currentState.version + 1,
              checksum: `gm-${crypto.randomUUID()}`,
              publicWorld: JSON.parse(v['world'] ?? '{}') as unknown,
              teams: JSON.parse(v['teams'] ?? '{}') as unknown,
            },
            broadcast: { headline: v['headline'] ?? '', publicMarkdown: v['publicMarkdown'] ?? '' },
            privateBulletins: JSON.parse(v['privateBulletins'] ?? '[]') as unknown,
          });
          const draft = await api<{ id: string }>(`${base}/rounds/${g.activeRoundNo}/drafts`, {
            expectedRoundVersion: e.roundVersion,
            content,
            ...(e.externalAssistance ? { externalAssistance: e.externalAssistance } : {}),
          });
          if (official)
            await api(`${base}/rounds/${g.activeRoundNo}/approve`, {
              expectedGameVersion: e.version,
              expectedRoundVersion: e.roundVersion,
              draftId: draft.id,
            });
        }
        if (e.kind === 'reflection')
          await api(
            `${teamBase}/self-assessment`,
            {
              expectedGameVersion: e.version,
              score: Number(v['score']),
              whatMatteredMost: v['reflection'],
            },
            'PUT',
          );
        if (e.kind === 'recap') {
          const content: FinalRecapContent = {
            openingState: b.scenario.publicMarkdown,
            finalState: v['finalState'] ?? '',
            headlines: [
              ...b.broadcasts.map((x) => x.headline),
              'The world you created',
              'The next choices remain open',
              'A discussion, not a forecast',
            ].slice(0, 5),
            keyChanges: b.broadcasts.map((x) => x.publicMarkdown.slice(0, 1000)),
            turningPoints: [],
            organizationArcs: [],
            unresolvedRisks: [],
          };
          await api(`${base}/recap`, {
            expectedGameVersion: e.version,
            action: official ? 'APPROVE_AND_COMPLETE' : 'SAVE_DRAFT',
            content,
          });
        }
        if (e.kind === 'finish')
          await api('/api/board/finish', { expectedGameVersion: e.version, reason: v['reason'] });
      },
      e.kind === 'move'
        ? official
          ? 'Action Brief submitted. This is your team’s official move.'
          : 'Draft saved privately · not submitted. Review it, then submit your Action Brief.'
        : official
          ? 'Confirmed.'
          : 'Saved.',
    );
    if (success && e.kind === 'move' && !official && savedWorkspace) {
      setEditor({ ...e, workspaceVersion: savedWorkspace.workspaceVersion, review: true });
    } else if (success) close();
  };
  const command = (path: string, body: unknown, message?: string) =>
    void run(() => api(path, body), message);
  const singleTeam = b.teams.length === 1;
  const left = Math.ceil(b.teams.length / 2);
  const column = (id: string) => {
    const i = b.teams.findIndex((t) => t.teamId === id);
    return singleTeam ? 3 : i < left ? i + 1 : i + 2;
  };
  const cardStyle = (id: string): CSSProperties => {
    const i = b.teams.findIndex((t) => t.teamId === id);
    const laneWidth = '(100% - 110px) / var(--lanes)';
    return {
      gridColumn: column(id),
      '--connector-left':
        singleTeam || i >= left ? 'var(--axis)' : `calc((${i + 1}) * (${laneWidth}) - 10px)`,
      '--connector-right': singleTeam
        ? 'calc(100% - var(--axis) - clamp(80px, calc(100% - 600px), 330px) - 10px)'
        : i < left
          ? 'calc(100% - var(--axis))'
          : `calc(100% - (${i}) * (${laneWidth}) - 110px - 10px)`,
    } as CSSProperties;
  };
  const layout = {
    '--lanes': Math.max(1, b.teams.length),
    '--left': left,
    '--axis': singleTeam
      ? 'min(240px, 35%)'
      : 'calc((100% - 110px) / var(--lanes) * var(--left) + 55px)',
    '--live-columns': singleTeam
      ? 'var(--axis) clamp(80px, calc(100% - 600px), 330px) minmax(0, 1fr)'
      : `${'minmax(210px, 1fr) '.repeat(left)}110px ${'minmax(210px, 1fr) '.repeat(b.teams.length - left)}`,
  } as CSSProperties;
  const stage =
    g.phase === 'ORGANIZATION_CREATION'
      ? gm
        ? 'Foundation · Prepare the game'
        : 'Foundation · Create your organization'
      : g.phase === 'REVEAL'
        ? 'Foundation · Your starting hand'
        : g.phase === 'FINAL_DEBRIEF' || g.phase === 'COMPLETED'
          ? 'The world you created'
          : `Round ${g.activeRoundNo} · ${g.phase.replaceAll('_', ' ').toLowerCase()}`;
  return (
    <>
      <section className="live-status" aria-label="Game progress">
        {connectionError && <p role="alert">{connectionError} · Showing the last loaded board.</p>}
        <h2>{stage}</h2>
        <p>Saved game · updates every two seconds</p>
        {notice && <p role="status">{notice}</p>}
        {error && (
          <p role="alert">
            {error} Your open text is retained.{' '}
            <button onClick={() => void refresh()}>Refresh status</button>
          </p>
        )}
      </section>
      {g.phase === 'ROUND_CLOSED' && (
        <SponsorInterlude
          key={`${g.gameId}-${g.activeRoundNo}`}
          gm={gm}
          gameId={g.gameId}
          round={g.activeRoundNo ?? 1}
        />
      )}
      <details className="live-world" id="live-world">
        <summary>Current world · {b.scenario.title}</summary>
        <h3>Scenario</h3>
        <SafeMarkdown content={b.scenario.publicMarkdown} />
        <WorldSituation world={b.world} />
        <h3>Playing the game</h3>
        <p>
          Three rounds at most. Resources are abstract units; describe your commitment explicitly.
          Discuss and negotiate around the table. Your own AI service has no privileged game access;
          pasted content goes to that service.
        </p>
      </details>
      {(!gm || g.phase !== 'ORGANIZATION_CREATION' || b.teams.some((t) => t.confirmed)) && (
        <section
          className={`board-sheet live-sheet ${b.teams.length === 1 ? 'board-single-team' : ''}`}
          style={layout}
        >
          <div className="live-lanes live-team-headings" aria-label="Team columns">
            {b.teams.map((t) => (
              <div
                className="board-team-heading"
                key={t.teamId}
                style={{ gridColumn: column(t.teamId), '--team-color': '#387b7a' } as CSSProperties}
              >
                <i aria-hidden="true" />
                <strong>{t.name}</strong>
              </div>
            ))}
          </div>
          <div className="board-phase">
            <span>Foundation</span>
          </div>
          <div className="live-lanes">
            {b.teams
              .filter((t) => !gm || t.confirmed)
              .map((t) => (
                <article
                  className={`live-card ${t.teamId === teamId ? 'live-own' : ''}`}
                  key={t.teamId}
                  style={cardStyle(t.teamId)}
                >
                  <EventTime
                    at={t.confirmedAt}
                    label="Organization confirmed"
                    missing={t.confirmed ? 'Time not recorded' : 'Awaiting submission'}
                  />
                  <small>{t.teamId === teamId ? 'Your organization' : t.name}</small>
                  <h3>{t.name}</h3>
                  <p>{t.confirmed ? 'Organization confirmed' : 'Creating an organization'}</p>
                  {t.organization && <p>{t.organization.mission}</p>}
                  {g.phase === 'REVEAL' && (
                    <p>
                      {t.acknowledged
                        ? 'Hand acknowledged'
                        : t.assignment?.status === 'REVEALED'
                          ? 'Ready for team review'
                          : gm
                            ? t.assignment
                              ? 'Saved privately · reveal when ready'
                              : 'Hand needs preparation'
                            : 'Waiting for the GM to prepare and reveal your hand'}
                    </p>
                  )}
                </article>
              ))}
          </div>
          {!gm && own?.assignment && hand && (
            <div className="live-lanes live-hand-event">
              <article className="live-card live-own" style={cardStyle(own.teamId)}>
                <EventTime
                  at={own.handAcknowledgedAt ?? own.assignment.revealedAt}
                  label={own.handAcknowledgedAt ? 'Hand acknowledged' : 'Hand revealed'}
                />
                <small>Foundation · PRIVATE · {own.name}</small>
                <details className="team-hand" open={g.phase === 'REVEAL' && !own.acknowledged}>
                  <summary>Your starting hand and private intelligence</summary>
                  <h3>Resources</h3>
                  <dl className="team-hand-resources">
                    {Object.entries(hand.resources).map(([name, amount]) => (
                      <div key={name}>
                        <dt>{name.charAt(0).toUpperCase() + name.slice(1)}</dt>
                        <dd>{amount}</dd>
                      </div>
                    ))}
                  </dl>
                  <h3>Capabilities</h3>
                  {hand.capabilities.length ? (
                    <ul>
                      {hand.capabilities.map((text, i) => (
                        <li key={i}>{text}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>No capabilities listed.</p>
                  )}
                  <h3>Liabilities</h3>
                  {hand.liabilities.length ? (
                    <ul>
                      {hand.liabilities.map((text, i) => (
                        <li key={i}>{text}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>No liabilities listed.</p>
                  )}
                  <h3>Private intelligence</h3>
                  <p className="team-hand-intelligence">
                    {own.privateInfo || 'No private intelligence provided.'}
                  </p>
                </details>
                {g.phase === 'REVEAL' &&
                  own.assignment?.status === 'REVEALED' &&
                  !own.acknowledged && (
                    <button
                      onClick={() =>
                        command(
                          '/api/board/ack',
                          { expectedGameVersion: g.version },
                          'Hand acknowledged.',
                        )
                      }
                    >
                      Acknowledge hand
                    </button>
                  )}
              </article>
            </div>
          )}
          {b.broadcasts.map((broadcast) => (
            <div key={broadcast.id}>
              <div className="board-phase">
                <span>Round {broadcast.roundNo}</span>
              </div>
              <div className="live-lanes">
                {b.submissionHistory
                  .filter((m) => m.roundNo === broadcast.roundNo)
                  .map((m) => (
                    <article
                      className={`live-card ${m.teamId === teamId ? 'live-own' : ''}`}
                      key={`${m.teamId}-${m.version}`}
                      style={cardStyle(m.teamId)}
                    >
                      <EventTime at={m.submittedAt} label="Official move submitted" />
                      <h3>{b.teams.find((t) => t.teamId === m.teamId)?.name}</h3>
                      <small>Official move · {new Date(m.submittedAt).toLocaleString()}</small>
                      {m.brief ? (
                        <details>
                          <summary>PRIVATE · Read locked Action Brief</summary>
                          {Object.entries(m.brief).map(([k, v]) => (
                            <p key={k}>
                              <strong>{actionFields[k as keyof ActionBrief]}</strong>
                              <br />
                              {v}
                            </p>
                          ))}
                        </details>
                      ) : (
                        <p>Move submitted</p>
                      )}
                    </article>
                  ))}
              </div>
              <article className="live-broadcast">
                <small>
                  World Broadcast · <time>{new Date(broadcast.publishedAt!).toLocaleString()}</time>
                </small>
                <h3>{broadcast.headline}</h3>
                <p>{broadcast.publicMarkdown}</p>
              </article>
              {b.bulletins
                .filter((n) => n.roundNo === broadcast.roundNo)
                .map((n, i) => (
                  <article className="live-private" key={i}>
                    <strong>PRIVATE · {own?.name}</strong>
                    <p>{n.markdown}</p>
                  </article>
                ))}
            </div>
          ))}
          {g.activeRoundNo && (
            <>
              <div className="board-phase">
                <span>{stage}</span>
              </div>
              <div className="live-lanes">
                {b.teams.map((t) => (
                  <article
                    key={t.teamId}
                    style={cardStyle(t.teamId)}
                    className={`live-card ${t.teamId === teamId ? 'live-own' : ''} ${!t.submittedAt ? 'live-pending' : ''}`}
                  >
                    <EventTime
                      at={t.submittedAt}
                      label="Official move submitted"
                      missing="Awaiting submission"
                    />
                    <h3>{t.name}</h3>
                    <p>
                      {t.submittedAt
                        ? `Official move accepted · ${new Date(t.submittedAt).toLocaleString()}`
                        : 'No official move this round'}
                    </p>
                    {g.phase === 'BETWEEN_ROUNDS' && (
                      <p>
                        {t.acknowledged ? 'Broadcast acknowledged' : 'Awaiting broadcast review'}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            </>
          )}
          {!gm && own && (
            <div className="live-lanes live-next-step-row">
              <div className="live-team-actions live-team-next-step" style={cardStyle(own.teamId)}>
                {g.phase === 'ORGANIZATION_CREATION' && (
                  <button className="board-primary" onClick={() => open('profile')}>
                    {own.confirmed ? 'Review organization' : 'Enter organization details'}
                  </button>
                )}
                {g.phase === 'REVEAL' && !own.assignment && (
                  <p>Your resources will appear here after the GM saves and reveals your hand.</p>
                )}
                {g.phase === 'ROUND_ACTIVE' && (
                  <>
                    {b.workspace?.officialSubmission && (
                      <p role="status">
                        <strong>Round {g.activeRoundNo} Action Brief submitted.</strong>
                        <br />
                        Accepted{' '}
                        {new Date(b.workspace.officialSubmission.submittedAt).toLocaleString()}.
                        Waiting for the GM to close the round and review consequences. No further
                        submission is needed.
                      </p>
                    )}
                    <p>
                      The last accepted official move before GM close is locked. You may replace it
                      before close.
                    </p>
                    {b.workspace?.currentDraft && !b.workspace.officialSubmission && (
                      <p>
                        Draft saved privately · not submitted. Review and submit it to make your
                        move official.
                      </p>
                    )}
                    <button className="board-primary" onClick={() => open('move')}>
                      {b.workspace?.officialSubmission
                        ? 'Edit a replacement Action Brief'
                        : b.workspace?.currentDraft
                          ? 'Review and submit Action Brief'
                          : 'Plan / review Action Brief'}
                    </button>
                  </>
                )}
                {b.workspace?.officialBrief && (
                  <details open={g.phase === 'ROUND_ACTIVE'}>
                    <summary>Your official move {b.workspace.locked ? '· LOCKED' : ''}</summary>
                    {Object.entries(b.workspace.officialBrief).map(([k, v]) => (
                      <p key={k}>
                        <strong>{actionFields[k as keyof ActionBrief]}</strong>
                        <br />
                        {v}
                      </p>
                    ))}
                  </details>
                )}
                {g.phase === 'BETWEEN_ROUNDS' && !own.acknowledged && (
                  <button
                    onClick={() =>
                      command(
                        '/api/board/ack',
                        { expectedGameVersion: g.version },
                        'Broadcast acknowledged.',
                      )
                    }
                  >
                    We have read this broadcast
                  </button>
                )}
                {g.phase === 'FINAL_DEBRIEF' && (
                  <>
                    <p>Original desired future: {own.organization?.desiredFuture}</p>
                    <button onClick={() => open('reflection')}>Reflect on your future</button>
                  </>
                )}
                {b.reflection && <p>PRIVATE reflection: {b.reflection.whatMatteredMost}</p>}
              </div>
            </div>
          )}
        </section>
      )}
      {gm && (
        <section className="live-gm">
          <h2>
            {g.phase === 'ORGANIZATION_CREATION'
              ? 'Prepare the game'
              : 'Game Master · next decision'}
          </h2>
          {g.phase === 'ORGANIZATION_CREATION' && (
            <div aria-label="Team readiness">
              <p>
                Review team requests, then wait for each team to confirm its organization before
                assigning resources.
              </p>
              <button onClick={onManageTeams}>Review team requests</button>
              {b.teams.length ? (
                <ul>
                  {b.teams.map((t) => (
                    <li key={t.teamId}>
                      <strong>{t.name}</strong> ·{' '}
                      {t.confirmed
                        ? 'Organization confirmed'
                        : 'Waiting for organization submission'}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No approved teams yet. Ask teams to request entry from Team entry.</p>
              )}
              {!b.teams.some((t) => t.confirmed) && (
                <p>
                  No timeline events yet. Confirmed organizations will appear here as teams submit
                  them.
                </p>
              )}
            </div>
          )}
          <button onClick={onManageTeams}>Game management</button>
          <p>Private controls. Read only the public World Broadcast to the room.</p>
          {g.phase === 'REVEAL' && (
            <div aria-label="Starting hands · next steps">
              <button
                disabled={busy || !b.teams.some((t) => t.assignment?.status !== 'REVEALED')}
                onClick={() => open('hand')}
              >
                {b.teams.length > 0 && b.teams.every((t) => t.assignment)
                  ? 'Review saved private hands'
                  : 'Prepare all teams’ private hands'}
              </button>
              {b.teams.some((t) => t.assignment?.status !== 'REVEALED') && (
                <>
                  <p>Save every team’s hand, then reveal all hands together for team review.</p>
                  <button
                    disabled={busy || !b.teams.length || b.teams.some((t) => !t.assignment)}
                    onClick={() =>
                      command(
                        `${base}/hands/reveal`,
                        { expectedGameVersion: g.version },
                        'All hands revealed. Waiting for teams to acknowledge them.',
                      )
                    }
                  >
                    Reveal all hands
                  </button>
                </>
              )}
              <p>
                {b.teams.every((t) => t.acknowledged)
                  ? 'All teams have acknowledged their hands. You can open Round 1.'
                  : 'Open Round 1 unlocks after every team acknowledges its revealed hand.'}
              </p>
            </div>
          )}
          {g.phase === 'ORGANIZATION_CREATION' && (
            <button
              disabled={busy || !b.teams.length || b.teams.some((t) => !t.confirmed)}
              onClick={() =>
                command(`${base}/setup/advance`, {
                  expectedGameVersion: g.version,
                  destination: 'REVEAL',
                })
              }
            >
              Begin resource assignment · close team entry
            </button>
          )}
          {g.phase === 'REVEAL' && (
            <button
              disabled={busy || b.teams.some((t) => !t.acknowledged)}
              onClick={() =>
                command(`${base}/setup/advance`, {
                  expectedGameVersion: g.version,
                  destination: 'ROUND_ACTIVE',
                })
              }
            >
              Open Round 1
            </button>
          )}
          {g.phase === 'ROUND_ACTIVE' && (
            <button
              disabled={busy}
              onClick={() =>
                command(
                  `${base}/rounds/${g.activeRoundNo}/close`,
                  { expectedGameVersion: g.version, expectedRoundVersion: b.gm?.roundVersion },
                  'Round closed. Moves are locked.',
                )
              }
            >
              Close round · lock accepted moves
            </button>
          )}
          {b.gm?.moves.map((m) => (
            <details key={m.teamId}>
              <summary>PRIVATE move · {b.teams.find((t) => t.teamId === m.teamId)?.name}</summary>
              {Object.entries(actionFields).map(([key, label]) => (
                <p key={key}>
                  <strong>{label}</strong>
                  <br />
                  {briefText(m.brief, key)}
                </p>
              ))}
            </details>
          ))}
          {(b.gm?.approvedConsequences || b.gm?.draft) && (
            <ConsequencePreview
              content={b.gm.approvedConsequences ?? b.gm.draft!.content}
              teams={b.teams}
              approved={!!b.gm.approvedConsequences}
              published={b.broadcasts.some((broadcast) => broadcast.roundNo === g.activeRoundNo)}
              round={g.activeRoundNo}
            />
          )}
          {b.gm?.consequenceHistory?.map(({ roundNo, content }) => (
            <details key={roundNo}>
              <summary>Round {roundNo} · saved consequences</summary>
              <ConsequencePreview
                content={content}
                teams={b.teams}
                approved
                published={b.broadcasts.some((broadcast) => broadcast.roundNo === roundNo)}
                round={roundNo}
              />
            </details>
          ))}
          {g.phase === 'ROUND_CLOSED' && (
            <>
              <button disabled={busy} onClick={() => open('resolution')}>
                Check round with AI
              </button>
              <button disabled={busy} onClick={() => open('resolution')}>
                Review / edit consequences
              </button>
              <p>
                AI proposes only. Check public text for private facts, feasibility, costs and
                conflicting actions.
              </p>
              {b.gm?.proposal && (
                <p>
                  AI: {b.gm.proposal.status} {b.gm.proposal.model} {b.gm.proposal.error}
                </p>
              )}
            </>
          )}
          {g.phase === 'ROUND_RESOLVED' && (
            <>
              <p>Approved. Teams still see the last published world.</p>
              <button
                disabled={busy}
                onClick={() =>
                  command(
                    `${base}/rounds/${g.activeRoundNo}/publish`,
                    { expectedGameVersion: g.version, expectedRoundVersion: b.gm?.roundVersion },
                    'World Broadcast published.',
                  )
                }
              >
                Publish approved World Broadcast
              </button>
            </>
          )}
          {g.phase === 'BETWEEN_ROUNDS' && (g.activeRoundNo ?? 0) < 3 && (
            <button
              disabled={busy || b.teams.some((t) => !t.acknowledged)}
              onClick={() =>
                command(`${base}/advance`, {
                  expectedGameVersion: g.version,
                  destination: 'NEXT_ROUND',
                })
              }
            >
              Open Round {(g.activeRoundNo ?? 0) + 1}
            </button>
          )}
          {g.phase === 'BETWEEN_ROUNDS' && g.activeRoundNo === 3 && (
            <button
              onClick={() =>
                command(`${base}/advance`, {
                  expectedGameVersion: g.version,
                  destination: 'FINAL_DEBRIEF',
                })
              }
            >
              Begin final debrief
            </button>
          )}
          {!['ORGANIZATION_CREATION', 'COMPLETED', 'FINAL_DEBRIEF', 'ROUND_RESOLVED'].includes(
            g.phase,
          ) && <button onClick={() => open('finish')}>Finish early with reason</button>}
          {g.phase === 'FINAL_DEBRIEF' && (
            <>
              <p>
                {b.gm?.reflections.length} / {b.teams.length} reflections received. Give teams time
                before completing.
              </p>
              {b.gm?.reflections.map((r) => (
                <p key={r.teamId}>
                  PRIVATE · {b.teams.find((t) => t.teamId === r.teamId)?.name}: {r.whatMatteredMost}
                </p>
              ))}
              <button onClick={() => open('recap')}>Review final world / complete game</button>
            </>
          )}
        </section>
      )}
      {(g.phase === 'FINAL_DEBRIEF' || g.phase === 'COMPLETED') && (
        <article className="live-broadcast">
          <h2>The world you created</h2>
          {b.finishReason && <p>Early ending: {b.finishReason}</p>}
          <>
            {b.recap?.content.finalState ? (
              <SafeMarkdown content={b.recap.content.finalState} />
            ) : (
              <WorldSituation world={b.world} />
            )}
          </>
          <p>
            {g.phase === 'COMPLETED'
              ? 'Game complete. History remains available.'
              : 'Discuss what changed, what surprised you, and what you would do next.'}
          </p>
        </article>
      )}
      <dialog
        className="board-dialog live-dialog"
        ref={dialog}
        onClose={() => setEditor(undefined)}
      >
        <header>
          <h2>
            {editor?.kind === 'move'
              ? 'Action Brief'
              : editor?.kind === 'resolution'
                ? 'GM consequence review'
                : editor?.kind === 'profile'
                  ? 'Organization profile'
                  : editor?.kind === 'hand'
                    ? 'Prepare all teams’ private hands'
                    : editor?.kind === 'reflection'
                      ? 'Final reflection'
                      : editor?.kind === 'finish'
                        ? 'Finish early'
                        : 'Final world'}
          </h2>
          <button onClick={close} aria-label="Close editor">
            ×
          </button>
        </header>
        {editor && (
          <>
            <p>
              {editor.kind === 'move'
                ? editor.review
                  ? b.workspace?.officialSubmission
                    ? 'Your team already submitted this round. Submit a replacement only if you want to change the accepted move.'
                    : 'Review your Action Brief below, then choose Submit Action Brief to make it official.'
                  : 'Saving keeps a private draft. Review and submit it to make your team’s official move.'
                : editor.review
                  ? 'Review before making this official.'
                  : 'Your edits are private until explicitly confirmed or published.'}
            </p>
            {error && (
              <p role="alert">
                {error} Text retained. Close and reopen to load the latest saved version.
              </p>
            )}
            {gm && editor.kind === 'resolution' && !editor.review && (
              <RoundAssistantExchange
                board={b}
                source={editor.externalAssistance}
                onFill={(values, source) =>
                  setEditor((current) =>
                    current
                      ? {
                          ...current,
                          values: { ...current.values, ...values },
                          externalAssistance: source,
                        }
                      : current,
                  )
                }
              />
            )}
            {gm && editor.kind === 'hand' && (
              <GmHandExchange
                board={b}
                onSave={async (team, fields) => {
                  const [hand] = parseHands(
                    JSON.stringify({
                      teams: [
                        {
                          teamId: team.teamId,
                          resources: JSON.parse(fields.resources) as unknown,
                          capabilities: fields.capabilities.split('\n').filter(Boolean),
                          liabilities: fields.liabilities.split('\n').filter(Boolean),
                          privateInfo: fields.privateInfo,
                          rationale: fields.rationale,
                        },
                      ],
                    }),
                    [team.teamId],
                  );
                  const before = await entryApi<BoardSnapshot>('/api/board');
                  const current = before.teams.find((t) => t.teamId === team.teamId);
                  if (
                    current?.version !== team.version ||
                    current.assignment?.status === 'REVEALED'
                  )
                    throw new Error(
                      'This hand changed. Close and reopen to review the saved version. Your text is retained here.',
                    );
                  await api('/api/board/private-info', {
                    expectedGameVersion: before.game.version,
                    teamId: team.teamId,
                    text: hand!.privateInfo,
                  });
                  const fresh = await entryApi<BoardSnapshot>('/api/board');
                  await api(`${base}/teams/${team.teamId}/assignment`, {
                    action: 'ADJUST',
                    expectedGameVersion: fresh.game.version,
                    expectedTeamVersion: team.version,
                    adjustmentReason: hand!.rationale,
                    assignment: {
                      resources: hand!.resources,
                      capabilities: hand!.capabilities,
                      liabilities: hand!.liabilities,
                    },
                  });
                  const updated = await refresh();
                  const savedTeam = updated?.teams.find((t) => t.teamId === team.teamId);
                  if (!savedTeam)
                    throw new Error(
                      'Save sent, but refresh failed. Close and reopen to check saved state before retrying.',
                    );
                  return savedTeam;
                }}
              />
            )}
            {!gm &&
              teamId &&
              !editor.review &&
              (editor.kind === 'profile' ||
                editor.kind === 'move' ||
                editor.kind === 'reflection') && (
                <TeamAssistantExchange
                  key={`${editor.kind}-${editor.roundNo}`}
                  kind={editor.kind}
                  board={b}
                  teamId={teamId}
                  values={editor.values}
                  onFill={(fields) =>
                    setEditor((current) =>
                      current
                        ? { ...current, review: false, values: { ...current.values, ...fields } }
                        : current,
                    )
                  }
                />
              )}
            {Object.entries(editor.values)
              .filter(
                ([k]) =>
                  editor.kind !== 'hand' && (editor.kind !== 'profile' || k in profileFields),
              )
              .map(([k, v]) => (
                <label key={k}>
                  <span id={`live-field-${k}`}>
                    {editor.kind === 'move'
                      ? actionFields[k as keyof ActionBrief]
                      : editor.kind === 'profile'
                        ? profileFields[k as keyof typeof profileFields]
                        : ((
                            {
                              summary: 'GM-only assessment',
                              headline: 'PUBLIC headline',
                              publicMarkdown: 'PUBLIC consequences',
                              world: 'PUBLIC world facts (JSON)',
                              teams: 'PRIVATE team states (JSON)',
                              privateBulletins: 'PRIVATE notes (JSON: teamId, markdown)',
                              privateInfo: 'PRIVATE intelligence',
                              resources: 'Resources (JSON)',
                              capabilities: 'Capabilities · one per line',
                              liabilities: 'Liabilities · one per line',
                              reflection: 'What mattered most?',
                              score: 'How close did you get? −5 to +5',
                              finalState: 'PUBLIC final world',
                              reason: 'Reason for ending early',
                            } as Record<string, string>
                          )[k] ?? k)}
                  </span>
                  <textarea
                    aria-labelledby={`live-field-${k}`}
                    value={v}
                    readOnly={editor.review}
                    disabled={busy}
                    onChange={(e) => set(k, e.target.value)}
                    rows={k === 'teams' ? 10 : 3}
                  />
                </label>
              ))}
            <div className="board-dialog-actions">
              <button
                hidden={editor.kind === 'hand' || (editor.kind === 'move' && editor.review)}
                disabled={busy}
                onClick={() => void save(false)}
              >
                {editor.kind === 'move'
                  ? 'Save draft'
                  : editor.kind === 'profile'
                    ? 'Confirm organization'
                    : editor.kind === 'finish'
                      ? 'Begin debrief'
                      : 'Save'}
              </button>
              {['move', 'resolution', 'recap'].includes(editor.kind) &&
                (!editor.review ? (
                  <button disabled={busy} onClick={() => setEditor({ ...editor, review: true })}>
                    {editor.kind === 'move'
                      ? 'Review and submit Action Brief'
                      : 'Review before confirming'}
                  </button>
                ) : (
                  <>
                    <button onClick={() => setEditor({ ...editor, review: false })}>
                      Continue editing
                    </button>
                    <button
                      className="board-primary"
                      disabled={busy}
                      onClick={() => void save(true)}
                    >
                      {editor.kind === 'move'
                        ? b.workspace?.officialSubmission
                          ? 'Submit replacement Action Brief'
                          : 'Submit Action Brief'
                        : editor.kind === 'resolution'
                          ? 'Approve consequences'
                          : 'Approve final world and complete'}
                    </button>
                  </>
                ))}
            </div>
          </>
        )}
      </dialog>
    </>
  );
}
